"use client";

import { useTranslations } from "next-intl";
import {
  Armchair,
  BatteryCharging,
  Bird,
  Car,
  CarTaxiFront,
  Clock,
  Coffee,
  Compass,
  MapPin,
  Mountain,
  PawPrint,
  Phone,
  Plane,
  Ship,
  Stethoscope,
  Store,
  TreePine,
  Wifi,
} from "lucide-react";
import { RegionalLayout } from "@/components/RegionalLayout";
import {
  RegionalHero,
  RegionalInfoStrip,
  SectionHeader,
} from "@/components/RegionalHome";
import { mpdaConfig } from "./config";

// Imágenes para el carrusel del hero
const CAROUSEL_IMAGES = [
  "https://galeria.tocumenaero.com/gallery/regionales/MPDA.JPG",
  "https://galeria.tocumenaero.com/gallery/api/Aeropuerto%20Enrique%20Malek%20de%20Chiriqu%C3%AD/20200928063151_9M1A5861.jpg",
  "https://galeria.tocumenaero.com/gallery/api/Aeropuerto%20Enrique%20Malek%20de%20Chiriqu%C3%AD/20200928065958_9M1A5897.jpg",
];

const SERVICE_ICONS = [
  Plane,
  Armchair,
  Stethoscope,
  Wifi,
  BatteryCharging,
  PawPrint,
] as const;

const TURISMO_ICONS = [Mountain, TreePine, Ship, Compass, Bird, Store] as const;

type Operacion = {
  title: string;
  badge: string;
  desc: string;
};

type Item = {
  title: string;
  desc: string;
};

export default function MpdaPage() {
  const t = useTranslations("mpda");

  const operaciones = t.raw("operaciones.items") as Operacion[];
  const services = t.raw("servicios.items") as Item[];
  const turismo = t.raw("turismo.items") as Item[];
  const transporteOptions = t.raw(
    "instalaciones.transporte.options",
  ) as string[];

  const infoItems = [
    {
      icon: MapPin,
      label: t("info.ubicacionLabel"),
      value: t("info.ubicacionValue"),
    },
    {
      icon: Clock,
      label: t("info.horarioLabel"),
      value: t("info.horarioValue"),
    },
    {
      icon: Phone,
      label: t("info.telefonoLabel"),
      value: mpdaConfig.contact.phone,
      href: `tel:${mpdaConfig.contact.phone}`,
    },
    {
      icon: Wifi,
      label: t("info.wifiLabel"),
      value: t("info.wifiValue"),
    },
  ];

  return (
    <RegionalLayout config={mpdaConfig}>
      <div className="bg-white">
        <RegionalHero
          images={CAROUSEL_IMAGES}
          eyebrow={t("hero.eyebrow")}
          title={t("hero.title")}
          subtitle={t("hero.subtitle")}
        />

        <RegionalInfoStrip items={infoItems} />

        {/* Destinos y operaciones */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("operaciones.eyebrow")}
              title={t("operaciones.title")}
              subtitle={t("operaciones.subtitle")}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {operaciones.map((operacion) => (
                <div
                  key={operacion.title}
                  className="bg-white p-8 transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <Plane className="w-6 h-6 text-[#102542]" />
                    <span className="shrink-0 px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                      {operacion.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight mb-2">
                    {operacion.title}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {operacion.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Servicios de la terminal */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("servicios.eyebrow")}
              title={t("servicios.title")}
              subtitle={t("servicios.subtitle")}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {services.map((service, index) => {
                const Icon = SERVICE_ICONS[index] ?? Plane;
                return (
                  <div
                    key={service.title}
                    className="bg-white p-8 transition-colors hover:bg-gray-50"
                  >
                    <Icon className="w-6 h-6 text-[#102542] mb-4" />
                    <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {service.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Instalaciones */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("instalaciones.eyebrow")}
              title={t("instalaciones.title")}
              subtitle={t("instalaciones.subtitle")}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {/* Estacionamiento */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <Car className="w-6 h-6 text-[#102542]" />
                  <span className="px-3 py-1 rounded-full border border-gray-300 text-xs uppercase tracking-wider font-semibold text-[#102542]">
                    {t("instalaciones.estacionamiento.badge")}
                  </span>
                </div>
                <p className="text-4xl font-bold text-[#102542] tracking-tight tabular-nums mb-1">
                  {t("instalaciones.estacionamiento.metric")}
                </p>
                <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
                  {t("instalaciones.estacionamiento.metricLabel")}
                </p>
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.estacionamiento.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("instalaciones.estacionamiento.desc")}
                </p>
              </div>

              {/* Cafeterías */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <Coffee className="w-6 h-6 text-[#102542] mb-6" />
                <p className="text-4xl font-bold text-[#102542] tracking-tight tabular-nums mb-1">
                  {t("instalaciones.cafeterias.metric")}
                </p>
                <p className="text-xs font-mono uppercase tracking-wider text-gray-400 mb-4">
                  {t("instalaciones.cafeterias.metricLabel")}
                </p>
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.cafeterias.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {t("instalaciones.cafeterias.desc")}
                </p>
              </div>

              {/* Transporte */}
              <div className="bg-white p-8 transition-colors hover:bg-gray-50">
                <CarTaxiFront className="w-6 h-6 text-[#102542] mb-6" />
                <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                  {t("instalaciones.transporte.title")}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed mb-4">
                  {t("instalaciones.transporte.desc")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {transporteOptions.map((option) => (
                    <span
                      key={option}
                      className="px-3 py-1 rounded-full border border-gray-300 text-xs text-gray-700 transition-colors hover:border-[#102542]"
                    >
                      {option}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Turismo en Chiriquí */}
        <section className="py-20 border-t border-gray-200">
          <div className="max-w-[80%] mx-auto">
            <SectionHeader
              eyebrow={t("turismo.eyebrow")}
              title={t("turismo.title")}
              subtitle={t("turismo.subtitle")}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {turismo.map((item, index) => {
                const Icon = TURISMO_ICONS[index] ?? Mountain;
                return (
                  <div
                    key={item.title}
                    className="bg-white p-8 transition-colors hover:bg-gray-50"
                  >
                    <Icon className="w-6 h-6 text-[#102542] mb-4" />
                    <h3 className="font-bold text-gray-900 tracking-tight mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </RegionalLayout>
  );
}
